import React, { useEffect, useState } from "react";
import { PropTypes } from "prop-types";
import {
  Box,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Paper,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowBack, SpeakerNotes, SaveAlt } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
  homeworkFeedbackResponseTypes,
  officeMimeTypes,
  pdfMimeType
} from "../../../../constants";
import {
  filesPreloading,
  uploadFile
} from "../../../../services/fileUploadService";
import PdfViewer from "../../../../components/PdfViewer/PdfViewer";
import { useHomeworkSubmissionComment } from "../../../../hooks/homework";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import GoogleDocsViewer from "../../../../components/GoogleDocsViewer/GoogleDocsViewer";
import VideoPlayer from "../../../../components/VideoPlayer/VideoPlayer";
import HomeworkFeedbackPanel from "./HomeworkFeedbackPanel";
import { homeworkSubmissionTypes } from "../../../../constants/index";
import Tabs from "../../../../components/Tabs/Tabs";
import SwipeableViews from "react-swipeable-views";
import usePermissions from "../../../../redux/selectors/usePermissions";

const feedbackPanelWidth = 444;
const tabsHeight = 104;

const filePreviewTypes = {
  doc: "doc",
  video: "video",
  image: "image",
  pdf: "pdf"
};

const useStyles = makeStyles(theme => ({
  appBar: {
    height: theme.sizes.topNavBarHeight,
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: theme.palette.common.white,
    boxShadow: "0px 7px 10px -10px rgba(0,0,0,0.3)"
  },
  previewContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    paddingBottom: theme.spacing(1),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  feedbackPanelExpanded: {
    width: `calc(100% - ${feedbackPanelWidth}px)`,
    padding: theme.spacing(0, 4),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2)
    }
  },
  feedbackPanelCollapsed: {
    width: "100%",
    padding: theme.spacing(0, 10),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0, 2)
    }
  },
  submissionLink: {
    ...theme.typography.h4,
    ...theme.components.MuiTypography.styleOverrides.h4,
    color: theme.palette.text.primary,
    textDecoration: "none",
    padding: theme.spacing(0, 0, 0.25, 2),
    "&:hover": {
      textDecoration: "underline"
    }
  },
  previewImage: {
    maxWidth: "100%",
    objectFit: "contain"
  },
  previewPaper: {
    padding: theme.spacing(3, 6, 4, 6),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2)
    }
  }
}));

const HomeworkSubmissionPreview = ({
  fileType,
  filePath,
  submissionType,
  submissionText
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation();
  const [currentSubmissionTypeTab, setCurrentSubmissionTypeTab] = useState(0);
  const [previewFileType, setPreviewFileType] = useState(null);

  const isLoadingPreview = useSelector(
    ({ contentPlayerState }) => contentPlayerState.showSpinner
  );

  useEffect(() => {
    if (fileType) {
      if (officeMimeTypes.includes(fileType)) {
        setPreviewFileType(filePreviewTypes.doc);
      } else if (fileType === pdfMimeType) {
        setPreviewFileType(filePreviewTypes.pdf);
      } else if (fileType.indexOf("video") !== -1) {
        setPreviewFileType(filePreviewTypes.video);
      } else if (fileType.indexOf("image") !== -1) {
        setPreviewFileType(filePreviewTypes.image);
      } else {
        // if there's no previewer available for the file type (ex. zip file), we automatically download the file
        window.location = filePath;
        setPreviewFileType("");
      }
    }
  }, [fileType]);

  const isFileAndTextSubmission =
    submissionType === homeworkSubmissionTypes.fileAndText;

  // Need exact height for google docs viewwer
  const previewHeight = `${window.innerHeight -
    theme.sizes.topNavBarHeight -
    (isFileAndTextSubmission ? tabsHeight : 0) -
    theme.spacing(15)}`;

  const filePreview = (
    <>
      {previewFileType === filePreviewTypes.doc && (
        <>
          {isLoadingPreview && <LoadingSpinner />}
          <GoogleDocsViewer height={previewHeight} url={filePath} />
        </>
      )}
      {previewFileType === filePreviewTypes.pdf && (
        <PdfViewer link={filePath} />
      )}
      {previewFileType === filePreviewTypes.video && (
        <VideoPlayer mimeType={fileType} contentLink={filePath} disableMargin />
      )}
      {previewFileType === filePreviewTypes.image && (
        <img
          src={filePath}
          alt="Homework Submission"
          className={classes.previewImage}
        />
      )}
      {previewFileType === "" && (
        <Typography variant="body1" sx={{ textAlign: "center", py: 2 }}>
          {t("homeworkFeedbackEditor.noPreview")}
        </Typography>
      )}
    </>
  );
  const tabs = [t("homeworkFeedbackEditor.fileSubmission")];
  if (isFileAndTextSubmission) {
    tabs.push(t("homeworkFeedbackEditor.textSubmission"));
  }

  return (
    <Paper className={classes.previewPaper}>
      <Tabs
        tabs={tabs}
        currentTab={currentSubmissionTypeTab}
        onChange={setCurrentSubmissionTypeTab}
        variant={isFileAndTextSubmission ? "fullWidth" : "standard"}
        disableHighlight
        centered
      />

      <SwipeableViews index={currentSubmissionTypeTab}>
        <Box pt={4}>{filePreview}</Box>
        <Box pt={4}>
          {submissionText ? (
            <div
              dangerouslySetInnerHTML={{
                __html: submissionText
              }}
            />
          ) : (
            <Typography variant="body1" sx={{ textAlign: "center", pt: 2 }}>
              {t("homeworkFeedbackEditor.noTextSubmission")}
            </Typography>
          )}
        </Box>
      </SwipeableViews>
    </Paper>
  );
};

const HomeworkFeedback = ({ onClose, submissionData, legacy = false }) => {
  const theme = useTheme();
  const classes = useStyles();
  const { t } = useTranslation();
  const [isUpdatingComment, setIsUpdatingComment] = useState(false);
  const { isAdminOrInstructor } = usePermissions();
  const [feedbackPanelExpanded, setFeedbackPanelExpanded] = useState(
    isAdminOrInstructor && !legacy
  );
  const [existingComment, setExistingComment] = useState(null);
  const {
    updateHomeworkSubmissionComment,
    createHomeworkSubmissionComment
  } = useHomeworkSubmissionComment();

  useEffect(() => {
    if (submissionData) {
      setExistingComment(
        submissionData.comments ? submissionData.comments[0] : null
      );
    }
  }, [submissionData]);

  const handleSaveFeedback = commentData => async publish => {
    setIsUpdatingComment(true);
    const newCommentData = {};
    if (
      commentData.responseType ===
        homeworkFeedbackResponseTypes.uploadedResponse &&
      commentData.file
    ) {
      const uploadedFile = await uploadFile(commentData.file);
      newCommentData.fileId = uploadedFile.fileId;
    } else {
      // upload images/media from WYSIWYG editor
      const contentUploads = await filesPreloading(
        commentData.contentFileList,
        commentData.content
      );
      newCommentData.contentFileList = contentUploads;
      newCommentData.content = contentUploads.content;
    }
    newCommentData.publish = publish;
    let updatedComment = null;
    if (!!existingComment) {
      updatedComment = await updateHomeworkSubmissionComment(
        submissionData.id,
        existingComment.id,
        newCommentData
      );
    } else {
      updatedComment = await createHomeworkSubmissionComment(
        submissionData.id,
        newCommentData
      );
    }
    setExistingComment(updatedComment);
    setIsUpdatingComment(false);
  };

  const isPublished = existingComment && !!existingComment.publishedAt;

  return (
    <Box
      position="fixed"
      width="100vw"
      minHeight="100vh"
      height="100vh"
      left={0}
      top={0}
      zIndex={1201}
      bgcolor={theme.palette.body}
      display="flex"
      overflow="auto"
      data-qa="homework-feedback"
    >
      <Box height="100%" flex={1}>
        <AppBar position="fixed" className={classes.appBar}>
          <Toolbar>
            <Box display="flex" flex="1" justifyContent="space-between">
              <Box
                display="flex"
                alignItems="center"
                pt={1.5}
                color={theme.palette.text.primary}
              >
                <IconButton
                  onClick={onClose}
                  data-qa="homework-feedback-back-btn"
                  size="large"
                >
                  <ArrowBack color="action" />
                </IconButton>
                <a
                  className={classes.submissionLink}
                  href={submissionData.filePath}
                  download
                >
                  {legacy && <i>Legacy submission: </i>}
                  {submissionData.filename}
                </a>
                <Box ml={1}>
                  <SaveAlt />
                </Box>
              </Box>
              {isAdminOrInstructor && !legacy && (
                <Button
                  color="primary"
                  onClick={() => setFeedbackPanelExpanded(true)}
                  endIcon={
                    <Box display="flex" alignItems="center" pl={1}>
                      <SpeakerNotes />
                    </Box>
                  }
                  size="large"
                  data-qa="admin-review-notes-button"
                >
                  {t("homeworkFeedbackEditor.provideFeedback")}
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
        {!legacy && (
          <HomeworkFeedbackPanel
            comment={existingComment}
            isPublished={isPublished}
            expanded={feedbackPanelExpanded}
            onClose={() => setFeedbackPanelExpanded(false)}
            onSaveFeedback={handleSaveFeedback}
            isSaving={isUpdatingComment}
          />
        )}
        <Box
          mt={13}
          px={feedbackPanelExpanded ? 4 : 10}
          data-qa="homework-feedback-preview-container"
          className={clsx(classes.previewContainer, {
            [classes.feedbackPanelExpanded]: feedbackPanelExpanded,
            [classes.feedbackPanelCollapsed]: !feedbackPanelExpanded
          })}
        >
          <HomeworkSubmissionPreview
            submissionType={submissionData.submissionType}
            submissionText={submissionData.text}
            fileType={submissionData.fileType}
            filePath={submissionData.filePath}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default HomeworkFeedback;

HomeworkFeedback.propTypes = {
  onClose: PropTypes.func
};
